import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AppLayout, AuthLayout } from './layouts';
import { useAuth } from './hooks';
import { WebSocketProvider } from './providers/WebSocketProvider';
import {
  Auth,
  Agents,
  AgentDetail,
  SessionReplay,
  BusinessCenter,
  Integrations,
  Apps,
  Workspace,
  MissionControl,
} from './screens';

const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isInitializing } = useAuth();

  if (isInitializing) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
};

const RedirectIfAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isInitializing } = useAuth();

  if (isInitializing) {
    return null;
  }

  if (isAuthenticated) {
    return <Navigate to="/mission-control" replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => (
  <Routes>
    <Route
      element={
        <RedirectIfAuth>
          <AuthLayout />
        </RedirectIfAuth>
      }
    >
      <Route path="/auth" element={<Auth />} />
    </Route>

    <Route
      element={
        <RequireAuth>
          <WebSocketProvider>
            <AppLayout />
          </WebSocketProvider>
        </RequireAuth>
      }
    >
      <Route path="/" element={<Navigate to="/mission-control" replace />} />
      <Route path="/mission-control" element={<MissionControl />} />
      <Route path="/agents" element={<Agents />} />
      <Route path="/agents/:id" element={<AgentDetail />} />
      <Route path="/agents/:agentId/sessions/:id" element={<SessionReplay />} />
      <Route path="/apps" element={<Apps />} />
      <Route path="/business" element={<BusinessCenter />} />
      <Route path="/integrations" element={<Integrations />} />
      <Route path="/workspace" element={<Workspace />} />
    </Route>

    <Route path="*" element={<Navigate to="/mission-control" replace />} />
  </Routes>
);

export default App;
